import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getEndpoint } from "../../hooks/useApi";
import { getCookie } from "../../hooks/useCookie";
import useCategories from "../../hooks/useCategories";
import useLanguages from "../../hooks/useLanguages";
import useAxios from "../../hooks/useAxios";
import { useTheme } from '@mui/material/styles';

import { TextField, Typography, Button, Checkbox, Box, OutlinedInput, InputLabel, MenuItem, FormControl, Select, Chip, Autocomplete } from '@mui/material';

import ReactQuill, { Quill } from 'react-quill';
import { useNavigate, useLocation } from "react-router-dom";

import 'react-quill/dist/quill.snow.css';

const API_ENDPOINT = getEndpoint();

const Module = Quill.import('core/module');
const BlockEmbed = Quill.import('blots/block/embed');

class ImageBlot extends BlockEmbed {
    static blotName = 'image';
    static tagName = ['figure', 'image'];

    static create(value) {
        let node = super.create();
        let img = window.document.createElement('img');
        if (value.alt || value.caption) {
            img.setAttribute('alt', value.alt || value.caption);
        }

        if (value.src || typeof value === 'string') {
            if (value.src?.startsWith('https://telegra.ph/') || value.src?.startsWith('https://images.menapress.org/')) {
                img.setAttribute('src', value.src);

            } else {
                img.setAttribute('src', '/images/loading.gif');

                axios.post(`${API_ENDPOINT}/utils/getTelegraphLinkByText`, {
                    image: value
                })
                    .then(res => {
                        if (res.data.link != null) {
                            img.setAttribute('src', res.data.link);
                        } else {
                            img.setAttribute('src', '/images/image-upload-error.png');
                            img.setAttribute('data-delete', true);
                        }
                    })
            }
        }
        node.appendChild(img);
        if (value.caption) {
            let caption = window.document.createElement('figcaption');
            caption.innerHTML = value.caption;
            node.appendChild(caption);
        }
        node.className = 'ql-card-editable ql-card-figure';
        return node;
    }

    constructor(node) {
        super(node);
        node.__onSelect = () => {
            if (!node.querySelector('input')) {
                let caption = node.querySelector('figcaption');
                let captionInput = window.document.createElement('input');
                captionInput.placeholder = 'Type your caption...';
                if (caption) {
                    captionInput.value = caption.innerText;
                    caption.innerHTML = '';
                    caption.appendChild(captionInput);
                } else {
                    caption = window.document.createElement('figcaption');
                    caption.appendChild(captionInput);
                    node.appendChild(caption);
                }
                captionInput.addEventListener('blur', () => {
                    let value = captionInput.value;
                    if (!value || value === '') {
                        caption.remove();
                    } else {
                        captionInput.remove();
                        caption.innerText = value;
                    }
                });
                captionInput.focus();
            }
        }
    }

    static value(node) {
        let img = node.querySelector('img');
        let figcaption = node.querySelector('figcaption');
        if (!img) return false;
        return {
            alt: img.getAttribute('alt'),
            src: img.getAttribute('src'),
            caption: figcaption ? figcaption.innerText : null
        };
    }
}

class CardEditableModule extends Module {
    constructor(quill, options) {
        super(quill, options);
        let listener = (e) => {
            if (!document.body.contains(quill.root)) {
                return document.body.removeEventListener('click', listener);
            }
            let elm = e.target.closest('.ql-card-editable');
            let deselectCard = () => {
                if (elm.__onDeselect) {
                    elm.__onDeselect(quill);
                } else {
                    quill.setSelection(quill.getIndex(elm.__blot.blot) + 1, 0, Quill.sources.USER);
                }
            }
            if (elm && elm.__blot && elm.__onSelect) {
                quill.disable();
                elm.__onSelect(quill);
                let handleKeyPress = (e) => {
                    if (e.keyCode === 27 || e.keyCode === 13) {
                        window.removeEventListener('keypress', handleKeyPress);
                        quill.enable(true);
                        deselectCard();
                    }
                }
                let handleClick = (e) => {
                    if (e.which === 1 && !elm.contains(e.target)) {
                        window.removeEventListener('click', handleClick);
                        quill.enable(true);
                        deselectCard();
                    }
                }
                window.addEventListener('keypress', handleKeyPress);
                window.addEventListener('click', handleClick);
            }
        };
        quill.emitter.listenDOM('click', document.body, listener);
    }
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

function getStyles(name, personName, theme) {
    return {
        fontWeight:
            personName.indexOf(name) === -1
                ? theme.typography.fontWeightRegular
                : theme.typography.fontWeightMedium,
    };
}

const AdminPost = () => {
    let navigate = useNavigate();
    const { state } = useLocation();

    const theme = useTheme();

    const [id, setId] = useState(null)
    const [title, setTitle] = useState('');
    const [customUrl, setCustomUrl] = useState('');
    const [description, setDescription] = useState('');
    const [freeContent, setFreeContent] = useState('');
    const [premiumContent, setPremiumContent] = useState('');
    // const freeAcsses = useRef(null);
    const [freeAcsses, setFreeAcsses] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [openingSentence, setOpeningSentence] = useState(null);
    const [showHeader, setShowHeader] = useState(false);
    const [showFooter, setShowFooter] = useState(false);
    const [headerContent, setHeaderContent] = useState('');
    const [footerContent, setFooterContent] = useState('');

    const [categories] = useCategories(true)
    const [languages, is_loading_languages] = useLanguages()

    const [isStatic, setIsStatic] = useState(false);
    const [isAds, setIsAds] = useState(false);

    const [editMode, setEditMode] = useState(false);
    const [response, error, loading, fetch] = useAxios();
    const [alertMessage, setAlertMessage] = useState();
    // const [confirmDelete, setConfirmDelete] = useState(false);
    const openingSentenceOptions = [
        'Nul n’a le privilège de toujours se tromper (Voltaire)',
        'Mal nommer les choses ajoute au malheur du monde (Camus)',
        'L’élément tragique de l’homme moderne, ce n’est pas qu’il ignore le sens de la vie, c’est que cela le dérange de moins en moins (Vaclav Havel)',
        'L’ennemi est bête : il croit que c’est nous l’ennemi alors que c’est lui (Pierre Desproges)',
        'Le pacifisme d’une religion ne se mesure pas au nombre de ses fidèles pacifiques mais à celui des assassins qui tuent en son nom (Stéphane Juffa)',
        'Si la liberté a un sens, elle signifie le droit de dire aux autres ce qu’ils n’ont pas envie d’entendre (George Orwell)',
        'Là où il n’y a pas d’espoir nous devons l’inventer (Albert Camus)',
        'Tant qu’il y a de l’espoir il y a de l’espoir (Stéphane Juffa)',
        'Le bonheur en partant m’a dit qu’il reviendrait (Jacques Prévert)',
        'On reconnaît le bonheur au bruit qu’il fait quand il s’en va (Jacques Prévert)'
    ];



    const modules = {
        cardEditable: true,
        toolbar: [
            [{ 'font': [] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
            // [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
            [{ 'align': [] }],

            ['link', 'image'],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote', 'script',
        'list', 'bullet', 'indent',
        'color', 'background', 'size', 'font',
        'link', 'image', 'align',
    ];

    // var Block = Quill.import('blots/block');
    // Block.tagName = 'DIV';
    // Quill.register(Block, true);

    Quill.register({
        // Other formats or modules
        'formats/image': ImageBlot,
        'modules/cardEditable': CardEditableModule,
    }, true);

    useEffect(() => {
        if (state?.edit) {
            setEditMode(true);
            const editArticle = state.editArticle

            console.log(editArticle);

            setId(editArticle.id)
            setCustomUrl(editArticle.path);
            setTitle(editArticle.title);
            setDescription(editArticle.description.replaceAll('<br>', "\n"));
            setFreeContent(editArticle.freeContent);
            setPremiumContent(editArticle.premiumContent);
            setHeaderContent(editArticle.headerContent);
            setShowHeader(editArticle.headerContent !== null && editArticle.headerContent !== '');
            setShowFooter(editArticle.footerContent !== null && editArticle.footerContent !== '');
            if (editArticle.footerContent !== null && editArticle.footerContent !== '') {
                setFooterContent(editArticle.footerContent);
            }
            setFreeAcsses(editArticle.freeAcsses);
            setSelectedCategories(editArticle.categories);
            setSelectedLanguages(editArticle.languages);
            setIsStatic(editArticle.isStatic);
            setIsAds(editArticle.isAds);
        }
        if (state?.editFromPreview) {
            const editArticle = state.editArticle
            setCustomUrl(editArticle.path);
            setTitle(editArticle.title);
            setDescription(editArticle.description);
            setFreeContent(editArticle.freeContent);
            setPremiumContent(editArticle.premiumContent);
            setFreeAcsses(editArticle.freeAcsses);
            setSelectedCategories(editArticle.categories);
            setSelectedLanguages(editArticle.languages);
            setHeaderContent(editArticle.headerContent);
            setFooterContent(editArticle.footerContent);
            setShowHeader(editArticle.showHeader);
            setShowFooter(editArticle.showFooter);
            setEditMode(editArticle.editMode);
            setOpeningSentence(editArticle.openingSentence);
            setIsStatic(editArticle.isStatic);
            setIsAds(editArticle.isAds);

        }
    }, [state])


    useEffect(() => {
        setFreeAcsses(premiumContent === '<p><br></p>')
    }, [premiumContent])

    const handelShowPreview = () => {
        // console.log(url.replace('https://telegra.ph/', ''));

        // let path = url.replace('https://telegra.ph/', '');

        const article = {
            title: title,
            path: customUrl,
            description: description,
            freeContent: freeContent,
            premiumContent: premiumContent,
            freeAcsses: freeAcsses,
            headerContent: headerContent,
            footerContent: footerContent,
            showHeader: showHeader,
            showFooter: showFooter,
            categories: selectedCategories,
            languages: selectedLanguages,
            editMode: editMode,
            openingSentence: openingSentence,
            isStatic: isStatic,
            isAds: isAds,
        };

        // setLocalPost(post);

        // navigate('/admin/preview-post');
        navigate('/admin/preview-post', { state: { article } });

    }

    useEffect(() => {

        let headerHtml = '';
        if (showHeader) {
            headerHtml = `<p class="ql-align-center"><span class="ql-font-serif notranslate" style="color: rgb(0, 51, 102);">La </span><strong class="ql-font-serif notranslate" style="color: rgb(255, 0, 0);">Mé</strong><strong class="ql-font-serif notranslate" style="color: rgb(0, 51, 102);">na</strong><span class="ql-font-serif" style="color: rgb(0, 51, 102);"> est une agence d’analyse, de ré-information et de reportage de proximité</span><span class="ql-font-serif" style="color: rgb(72, 72, 72);"> </span></p><p class="ql-align-center"><span class="ql-font-serif" style="color: rgb(0, 51, 102);">Copyright </span><strong class="ql-font-serif notranslate" style="color: rgb(255, 0, 0);">©</strong><span class="ql-font-serif" style="color: rgb(0, 51, 102);"> 2023 </span><strong class="ql-font-serif" style="color: rgb(255, 0, 0);">Me</strong><strong class="ql-font-serif notranslate" style="color: rgb(0, 51, 102);">tula </strong><strong class="ql-font-serif notranslate" style="color: rgb(255, 0, 0);">N</strong><strong class="ql-font-serif notranslate" style="color: rgb(0, 51, 102);">ews </strong><strong class="ql-font-serif notranslate" style="color: rgb(255, 0, 0);">A</strong><strong class="ql-font-serif notranslate" style="color: rgb(0, 51, 102);">gency</strong><span class="ql-font-serif" style="color: rgb(0, 51, 102);"> – Informations utiles en fin d’article</span><span class="ql-font-serif" style="color: rgb(72, 72, 72);"> </span></p>`;

            // if (openingSentence !== null) {
            headerHtml += `<p class="ql-align-center"><strong class="ql-font-serif" style="color: rgb(0, 51, 102);">${openingSentence ?? ''}</strong></p>`;
            // }
            headerHtml += `<p class="ql-align-center"><span class="ql-font-serif" style="color: rgb(0, 51, 102);">Pour s’acquitter du paiement de son abonnement à la </span><strong class="ql-font-serif" style="color: red;">Mé</strong><strong class="ql-font-serif" style="color: rgb(0, 51, 102);">na</strong><span class="ql-font-serif" style="color: rgb(0, 51, 102);">, cliquez</span><span class="ql-font-serif" style="color: rgb(72, 72, 72);"> </span><a href="https://menapress.org/pay" rel="noopener noreferrer" target="_blank" class="ql-font-serif">[ICI]</a></p>`
        }
        setHeaderContent(headerHtml);

    }, [openingSentence, showHeader])

    useEffect(() => {

        let footerHtml = '';
        if (showFooter) {
            footerHtml = '<h1 class="notranslate ql-align-justify"><span class="ql-font-serif" style="color: rgb(255, 0, 0);">Me</span><span class="ql-font-serif" style="color: rgb(2, 0, 120);">tula </span><span class="ql-font-serif" style="color: rgb(255, 0, 0);">N</span><span class="ql-font-serif" style="color: rgb(2, 0, 120);">ews</span> <span class="ql-font-serif" style="color: rgb(255, 0, 0);">A</span><span class="ql-font-serif" style="color: rgb(2, 0, 120);">gency </span><span class="ql-font-serif" style="color: rgb(255, 0, 0);">©</span><span class="ql-font-serif" style="color: rgb(2, 0, 120);"> </span></h1><p>&nbsp;</p><p class="ql-align-justify"><span style="color: rgb(0, 51, 102);">Cette diffusion est destinée aux professionnels de l’information aussi bien qu’aux personnes privées.</span></p><p class="ql-align-justify"><span style="color: rgb(0, 51, 102);"> </span></p><p class="ql-align-justify"><span style="color: rgb(0, 51, 102);"> </span><u>Toute reproduction entière ou partielle de cet article est strictement soumise à la détention d’un contrat ad hoc valide selon les conditions générales de la </u><u class="ql-font-serif" style="color: rgb(255, 0, 0);">Me</u><u class="ql-font-serif" style="color: rgb(2, 0, 120);">tula </u><u class="ql-font-serif" style="color: rgb(255, 0, 0);">N</u><u class="ql-font-serif" style="color: rgb(2, 0, 120);">ews </u><u class="ql-font-serif" style="color: rgb(255, 0, 0);">A</u><u class="ql-font-serif" style="color: rgb(2, 0, 120);">gency</u><u style="color: rgb(0, 51, 102);">. </u></p><p>&nbsp;</p><p><span style="color: rgb(0, 51, 102);">Le site de la </span><span class="ql-font-serif" style="color: rgb(255, 0, 0);">Mé</span><span class="ql-font-serif" style="color: rgb(2, 0, 120);">na</span><span style="color: rgb(0, 51, 102);"> : </span><a href="https://menapress.org/" rel="noopener noreferrer" target="_blank">www.menapress.org</a></p><p><span style="color: rgb(0, 51, 102);">Les e-mails de la</span> <span class="ql-font-serif" style="color: rgb(255, 0, 0);">Mé</span><span class="ql-font-serif" style="color: rgb(2, 0, 120);">na</span><span style="color: rgb(0, 51, 102);">&nbsp;:</span></p><p><span style="color: rgb(0, 51, 102);">&nbsp;</span></p><p class="ql-align-justify"><a href="mailto:redaction@metulanews.info" rel="noopener noreferrer" target="_blank">redaction@metulanews.info</a><span style="color: rgb(0, 51, 102);">&nbsp;pour communiquer avec notre rédaction.</span></p><p class="ql-align-justify"><a href="mailto:info@metulanews.info" rel="noopener noreferrer" target="_blank">info@metulanews.info</a><span style="color: rgb(0, 51, 102);">&nbsp;pour les questions administratives, commerciales et comptables.</span></p>'
        }
        setFooterContent(footerHtml);

    }, [showFooter])


    useEffect(() => {
        // window.alert('Error', error);
        if (!!error) {
            console.log(error)

            window.alert('Error:: ' + error);
        }
    }, [error])

    useEffect(() => {
        if (response !== null) {
            window.alert(alertMessage)
            navigate('/articles/' + customUrl);
        }
        console.log(response)
    }, [response])

    useEffect(() => {
        if (languages) setSelectedLanguages(languages);
    }, [languages])

    const handelPublish = () => {
        const email = getCookie("email");
        const token = getCookie("token");

        setAlertMessage('Article Added!');
        fetch({
            method: 'post',
            url: '/articles',
            body: {
                email: email,
                token: token,
                article: {
                    title: title,
                    path: customUrl,
                    description: description,
                    freeContent: freeContent,
                    premiumContent: premiumContent,
                    headerContent: headerContent,
                    footerContent: footerContent,
                    freeAcsses: freeAcsses,
                    categories: selectedCategories.map(c => c.id),
                    languages: selectedLanguages.map(l => l.id),
                    isStatic: isStatic,
                    isAds: isAds
                }
            }
        });
    }

    const handelUpdate = () => {
        const email = getCookie("email");
        const token = getCookie("token");

        setAlertMessage('Article Updated!');

        fetch({
            method: 'put',
            url: `/articles/${id}`,
            body: {
                email: email,
                token: token,
                article: {
                    id: id,
                    title: title,
                    path: customUrl,
                    description: description,
                    freeContent: freeContent,
                    premiumContent: premiumContent,
                    freeAcsses: freeAcsses,
                    headerContent: headerContent,
                    footerContent: footerContent,
                    categories: selectedCategories.map(c => c.id),
                    languages: selectedLanguages.map(l => l.id),
                    isStatic: isStatic,
                    isAds: isAds
                }
            }
        });
    }

    const handelDelete = () => {
        // if (!confirmDelete) {
        if (window.confirm('Are you sure you want to delete this article?')) {
            const email = getCookie("email");
            const token = getCookie("token");

            setAlertMessage('Article Deleted!');

            fetch({
                method: 'delete',
                url: `/articles/${id}`,
                body: {
                    email: email,
                    token: token,
                }
            });
        }
        // }
    }

    const handleChangeSelectedCategories = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedCategories(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        );
    }

    const handleChangeSelectedLanguages = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedLanguages(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        );
    }

    return (
        <>
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
                Admin Post Article Panel :
            </Typography>
            <TextField
                fullWidth
                required
                label="Title"
                // defaultValue=""
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ mb: 3 }}
            />

            <TextField
                fullWidth
                label="Custom url"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                sx={{ mb: 3 }}
            />

            <TextField
                fullWidth
                label="Description on main articles page"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                maxRows={4}
                sx={{ mb: 3 }}
            />

            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" component="span">
                    Show Header Mena
                </Typography>
                <Checkbox checked={Boolean(showHeader)} sx={{ mb: 1 }} onChange={(e) => { setShowHeader(e.target.checked) }} />
            </Box>

            {showHeader &&
                <>
                    <Autocomplete
                        disablePortal
                        freeSolo={true}
                        value={openingSentence}
                        onChange={(e, value) => setOpeningSentence(value)}
                        // id="combo-box-demo"
                        options={openingSentenceOptions}
                        sx={{ mb: 1 }}
                        renderInput={(params) => <TextField {...params} label="Opening Sentence" />}
                    />

                    <Box sx={{ mb: 1 }}>
                        <Typography variant="h6" component="span">
                            Header Content:
                        </Typography>
                        <ReactQuill
                            theme="snow"
                            value={headerContent}
                            onChange={setHeaderContent}
                            modules={modules}
                            formats={formats}
                        />
                    </Box>
                </>
            }

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="span">
                    Free Content:
                </Typography>
                <ReactQuill
                    theme="snow"
                    value={freeContent}
                    onChange={setFreeContent}
                    modules={modules}
                    formats={formats}
                />
            </Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="span">
                    Premium Content:
                </Typography>
                <ReactQuill
                    theme="snow"
                    value={premiumContent}
                    onChange={setPremiumContent}
                    modules={modules}
                    formats={formats}
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" component="span">
                    Show Footer Mena
                </Typography>
                <Checkbox checked={Boolean(showFooter)} sx={{ mb: 1 }} onChange={(e) => { setShowFooter(e.target.checked) }} />
            </Box>

            {showFooter &&
                <>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" component="span">
                            Footer Content:
                        </Typography>
                        <ReactQuill
                            theme="snow"
                            value={footerContent}
                            onChange={setFooterContent}
                            modules={modules}
                            formats={formats}
                        />
                    </Box>
                </>
            }

            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" component="span">
                    Free acsses to All users:
                </Typography>
                <Checkbox checked={Boolean(freeAcsses)} sx={{ mb: 1 }} onChange={(e) => { setFreeAcsses(e.target.checked) }} />
            </Box>


            <FormControl sx={{ mb: 3, minWidth: 250, width: '100%' }}>

                <InputLabel id="demo-multiple-chip-label">Categories</InputLabel>
                <Select
                    labelId="demo-multiple-chip-label"
                    id="demo-multiple-chip"
                    multiple
                    value={selectedCategories}
                    onChange={handleChangeSelectedCategories}
                    input={<OutlinedInput id="select-multiple-chip" label="Categories" />}
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                                <Chip key={value.id} label={value.title} />
                            ))}
                        </Box>
                    )}
                    MenuProps={MenuProps}
                >
                    {categories.map((category) => (
                        <MenuItem
                            key={category.id}
                            value={category}
                            style={getStyles(category.title, selectedCategories, theme)}
                        >
                            {category.title}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* <FormControl sx={{ mb: 3, minWidth: 250, width: '100%' }}>

                <InputLabel id="demo-multiple-chip-label">Languages</InputLabel>
                <Select
                    labelId="demo-multiple-chip-label"
                    id="demo-multiple-chip"
                    multiple
                    value={selectedLanguages}
                    onChange={handleChangeSelectedLanguages}
                    input={<OutlinedInput id="select-multiple-chip" label="Languages" />}
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                                <Chip key={value.id} label={value.title} />
                            ))}
                        </Box>
                    )}
                    MenuProps={MenuProps}
                >
                    {languages.map((language) => (
                        <MenuItem
                            key={language.id}
                            value={language}
                            style={getStyles(language.title, selectedLanguages, theme)}
                        >
                            {language.title}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl> */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" component="span">
                    is static article
                </Typography>
                <Checkbox checked={Boolean(isStatic)} sx={{ mb: 1 }} onChange={(e) => { setIsStatic(e.target.checked) }} />
            </Box>

            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" component="span">
                    is ads article
                </Typography>
                <Checkbox checked={Boolean(isAds)} sx={{ mb: 1 }} onChange={(e) => { setIsAds(e.target.checked) }} />
            </Box>


            <div className="add-post-buttons-container">
                {editMode &&
                    <>

                        <Button variant="contained" onClick={handelUpdate}>Update</Button>
                        <Button variant="contained" color="error" onClick={handelDelete}>Delete</Button>
                    </>
                }
                {!editMode &&
                    <Button variant="contained" onClick={handelPublish}>Publish</Button>
                }
                <Button variant="contained" color="secondary" onClick={handelShowPreview}>Preview</Button>

            </div>
        </>
    )
}

export default AdminPost;